/* eslint-disable react-hooks/exhaustive-deps -- Reset booking state when case changes (caseInfo.internalId); intentionally omit setState/clearAnswers to avoid unnecessary effect runs */
import './online-booking-page.css';
import { App, Button, Skeleton } from 'antd';
import { SalesCallCalendar } from '../../appointment-booking/sales-call-calendar';
import { useCallback, useEffect, useState } from 'react';
import {
  OpportunityComplete,
  CaseInfo,
  CaseStatus,
  Slot,
  SalesChannel,
  OpportunityEditContactDto,
  PreSalesQuestionnaireContext,
} from '../../../dto/model';
import { strings } from '../../../lang';
import { CustomerLostInfo, CustomerLostModal } from '../modal/customer-lost-modal';
import CustomerInfoSection from './online-booking-page-cust-info';
import { NotReachedModal } from '../modal/not-reached-modal';
import { ReachedButNotBookedBody, ReachedButNotBookedInfo } from '../modal/reached-not-booked';
import CaseCommentSection from './online-booking-page-case-comments';
import WrongPhoneNumberModal from '../modal/wrong-phone-number-modal';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken, HasRole, Role } from '../../../utils/auth-utils';
import { scopes } from '../../../authConfig';
import { bookingPageService } from '../../../services/booking-page-service';
import { useSalesChannelState } from '../../../store/header-state';
import { CalendarBackButton, LoadCalendarButton, StartBookingButton } from '../../appointment-booking/calendar-button';
import { QualifyingQuestions } from '../../appointment-booking/qualifying-questions';
import { QualifyingAnswer } from '../../../utils/lead-queue-utils';
import { useQualifyingAnswersState } from '../../../store/qualifying-answers-state';
import { useShallow } from 'zustand/shallow';
import { PreSalesQuestion } from '../../../dto/model';
import { PreSalesBookingWorkspace } from './pre-sales-booking-workspace';
import type { PreSalesCustomerInfoPatch } from './pre-sales-questionnaire';
import { buildCaseComplete } from '../case-complete-utils';
import { useLostReasonOptions } from './use-lost-reason-options';

const salesForceRootUrl = process.env.REACT_APP_SALESFORCE_BASE_URL ?? '';

const SALES_CHANNELS_WITH_LOOSE_CUSTOMER_LABEL: SalesChannel[] = [SalesChannel.Revision, SalesChannel.LeadQueue, SalesChannel.CrmSales];

function getLegacyCustomerLostButtonLabel(salesChannel: SalesChannel): string {
  return SALES_CHANNELS_WITH_LOOSE_CUSTOMER_LABEL.includes(salesChannel) ? strings.looseCustomerButton : strings.modalTitleCustomerLost;
}

type OnlineBookingPageProps = {
  caseInfo: CaseInfo | null;
  setCaseInfo: (caseInfo: CaseInfo) => void;
  cancelledSlot: Slot | null;
  inputCaseComments: string;
  setInputCaseComments: (inputComments: string) => void;
  onCaseClosed: (caseComplete: OpportunityComplete, bookedSlot?: Slot) => Promise<void>;
  inboundCenter: boolean | undefined;
  inboundAutodialed?: boolean;
  isAutodialerFlow?: boolean;
  isAutodialerHpFlow?: boolean;
  preSalesContext?: PreSalesQuestionnaireContext;
  preSalesQuestionnaire?: PreSalesQuestion[];
  onInboundCallback?: (bookedSlot: Slot | undefined) => void;
  bookingButtonStyle?: string;
  handleCallButtonClick: (phoneNumber: string) => void;
  onHpBooked?: () => void;
  /** Called when the presales form is submitted as a cancellation. Stops recording as ReachedButNotBooked. */
  onHpCancelled?: () => void;
};

export const OnlineBookingPage = ({
  caseInfo,
  cancelledSlot,
  inputCaseComments,
  setCaseInfo,
  setInputCaseComments,
  onCaseClosed,
  inboundCenter,
  inboundAutodialed,
  isAutodialerFlow,
  isAutodialerHpFlow,
  onHpBooked,
  preSalesContext,
  preSalesQuestionnaire,
  onInboundCallback,
  bookingButtonStyle,
  handleCallButtonClick,
  onHpCancelled,
}: OnlineBookingPageProps) => {
  const { notification } = App.useApp();
  const { instance, inProgress, accounts } = useMsal();
  const { salesChannel: salesManagerSalesChannel } = useSalesChannelState(useShallow((state) => ({ salesChannel: caseInfo?.salesChannel ?? state.salesChannel })));
  const [showWrongPhoneNumberModal, setShowWrongPhoneNumberModal] = useState(false);
  const [loadCalendar, setLoadCalendar] = useState(false);
  const [showCustomerLost, setShowCustomerLost] = useState(false);
  const [showNotReachedModal, setShowNotReachedModal] = useState(false);
  const [showReachedButNotBookedBody, setShowReachedButNotBookedBody] = useState(false);
  const [oppSalesChannel, setOppSalesChannel] = useState<SalesChannel | undefined>();
  const [showQualifyingQuestions, setShowQualifyingQuestions] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [storeAnswers, clearAnswers] = useQualifyingAnswersState(useShallow((state) => [state.answers, state.clearAnswers]));

  const {
    options: lostReasonOptions,
    ready: lostReasonsReady,
    configUnavailable: lostReasonConfigUnavailable,
  } = useLostReasonOptions({
    enabled: showCustomerLost,
    salesChannel: caseInfo?.salesChannel,
    configurable: caseInfo?.configurableLostReasonsInBooking,
    caseKey: caseInfo?.internalId,
  });

  const isAdTester = HasRole(accounts, Role.AutodialerTesters);
  const handlePreSalesCustomerInfoChange = useCallback((patch: PreSalesCustomerInfoPatch) => {
    if (!caseInfo) return;

    const hasChanged = Object.entries(patch).some(
      ([key, value]) => caseInfo[key as keyof PreSalesCustomerInfoPatch] !== value,
    );
    if (hasChanged) {
      setCaseInfo({ ...caseInfo, ...patch });
    }
  }, [caseInfo, setCaseInfo]);

  const query = new URLSearchParams(window.location.search);
  const sourceSystemType =
    query.get('type') === 'salescall2' ? 'salescall2' : query.get('type') === 'autodialer' || isAutodialerFlow ? 'autodialer' : inboundCenter ? 'inbound' : '';

  const getQualifyingAnswersForBooking = (): QualifyingAnswer[] => {
    return (
      caseInfo?.qualifyingQuestions
        ?.filter((q) => storeAnswers[q.question.id])
        .map((q) => ({
          questionId: q.question.id,
          questionText: JSON.stringify(q.question.questionText),
          answerText: storeAnswers[q.question.id],
        })) || []
    );
  };

  const buildBaseCaseComplete = (info: CaseInfo, overrides: Partial<OpportunityComplete>): OpportunityComplete =>
    buildCaseComplete(info, overrides, inputCaseComments);

  const onBooking = async (isSuccess: boolean, bookedSlot?: Slot, error?: string) => {
    if (!caseInfo) return;
    if (isSuccess) {
      await onCaseClosed(
        buildBaseCaseComplete(caseInfo, {
          caseStatus: CaseStatus.Booked,
          qualifyingAnswers: getQualifyingAnswersForBooking(),
        }),
        bookedSlot,
      );
      return;
    }
    if (error?.includes('case is already closed')) {
      await onCaseClosed(buildBaseCaseComplete(caseInfo, { caseStatus: CaseStatus.Booked }));
    }
  };

  const onUpdatePhoneNumber = async (phoneNumber: string) => {
    if (!caseInfo) return;
    setCaseInfo({ ...caseInfo, phone: phoneNumber });
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const editContactDto: OpportunityEditContactDto = {
      firstName: caseInfo.firstName ?? '',
      lastName: caseInfo.lastName ?? '',
      salutation: caseInfo.salutation ?? '',
      email: caseInfo.email ?? '',
      phone: phoneNumber ?? '',
      opportunityId: caseInfo.opportunityId ?? '',
      globalCustomerId: caseInfo.globalCustomerId ?? '',
      signUpId: caseInfo.signupId ?? '',
      contactId: caseInfo.contactId ?? '',
      city: caseInfo.city ?? '',
      street: caseInfo.street ?? '',
      houseNumber: caseInfo.houseNumber ?? '',
      zipCode: caseInfo.zipcode ?? '',
    };
    const updateResult = await bookingPageService.updateContact(editContactDto, salesManagerSalesChannel, token.accessToken);
    if (!updateResult.isSuccess) {
      notification.error({
        message: `Failed to update phone number. Errors: ${JSON.stringify(updateResult.errors)}`,
      });
      return;
    }
    notification.info({ message: 'Phone number updated successfully' });
  };

  const handleLoadCalendar = (channel: SalesChannel) => {
    setOppSalesChannel(channel);
    setLoadCalendar(true);
    setShowQualifyingQuestions(false);
    setShowBackButton(true);
  };

  const handleClickBack = () => {
    setLoadCalendar(false);
    setShowQualifyingQuestions(true);
    setShowBackButton(false);
  };

  useEffect(() => {
    setLoadCalendar(false);
    setInputCaseComments('');
    setShowBackButton(false);
    clearAnswers();
    if (caseInfo && !caseInfo.showQualifyingQuestions) {
      setOppSalesChannel(caseInfo.salesChannel === SalesChannel.LeadQueue ? SalesChannel.DigitalSales : (caseInfo.salesChannel ?? undefined));
    }
  }, [caseInfo?.internalId]);

  if (!caseInfo) {
    return <Skeleton />;
  }

  const useCloseCaseFlow = caseInfo.configurableLostReasonsInBooking === true && !lostReasonConfigUnavailable;

  const filteredCaseComments = caseInfo.caseComments
    .filter((item) => (caseInfo.salesChannel === SalesChannel.Revision ? !item.isCustomerLostReason : item.isNotBookedReason))
    .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

  return (
    <div className={isAutodialerHpFlow ? 'bp-online-booking-page bp-online-booking-page--presales' : 'bp-online-booking-page'}>
      {!isAutodialerHpFlow && (
        <CustomerInfoSection
          inboundCenter={inboundCenter}
          inboundAutodialed={inboundAutodialed}
          isAutodialerFlow={isAutodialerFlow}
          caseInfo={caseInfo}
          setCaseInfo={setCaseInfo}
          cancelledSlot={cancelledSlot}
          showWrongPhoneNumberModal={setShowWrongPhoneNumberModal}
          handleCallButtonClick={handleCallButtonClick}
        />
      )}
      {!isAutodialerHpFlow && <CaseCommentSection caseComments={filteredCaseComments} inputCaseComment={inputCaseComments} onInputCaseCommentChange={setInputCaseComments} shouldShowPreviousComments />}
      {isAutodialerHpFlow && (
        <PreSalesBookingWorkspace
          key={preSalesContext?.crmId ?? caseInfo.internalId}
          caseInfo={caseInfo}
          context={preSalesContext}
          questions={preSalesQuestionnaire ?? []}
          onBooked={onHpBooked}
          onCancelled={onHpCancelled}
          onCustomerInfoChange={handlePreSalesCustomerInfoChange}
        />
      )}

      {!isAutodialerHpFlow && <section className="bp-section">
        <div className={`bp-calendar__buttons${isAdTester ? '' : isAutodialerFlow ? ' bp-calendar__buttons--three' : ''}`}>
          {caseInfo.showQualifyingQuestions && !showQualifyingQuestions && !loadCalendar && <StartBookingButton buttonStyle={bookingButtonStyle} onClick={() => setShowQualifyingQuestions(true)} />}
          {!caseInfo.showQualifyingQuestions && !loadCalendar && <LoadCalendarButton buttonStyle={bookingButtonStyle} onClick={() => setLoadCalendar(true)} />}
          <Button onClick={() => setShowReachedButNotBookedBody(true)}>{strings.modalTitleReachedNotBooked}</Button>
          {(isAdTester || !isAutodialerFlow) && (
            <Button onClick={() => setShowNotReachedModal(true)}>
              {strings.modalTitleNotReached}
            </Button>
          )}
          <Button
            type="primary"
            danger
            onClick={() => setShowCustomerLost(true)}
          >
            {useCloseCaseFlow ? strings.completeCaseButton : getLegacyCustomerLostButtonLabel(caseInfo.salesChannel!)}
          </Button>
        </div>
      </section>}

      {caseInfo.showQualifyingQuestions && showQualifyingQuestions && (
        <section className="bp-calendar">
          <QualifyingQuestions questions={caseInfo.qualifyingQuestions} loadCalendar={handleLoadCalendar} buttonStyle={bookingButtonStyle} />
        </section>
      )}

      {showBackButton && <CalendarBackButton onClick={handleClickBack} />}

      {loadCalendar && (
        <section className="bp-section bp-calendar">
          <SalesCallCalendar
            opportunityId={caseInfo.opportunityId}
            signupId={caseInfo.signupId}
            onBooking={onBooking}
            skipConfirmationAfterBooking
            sourceSystemHeader={sourceSystemType}
            salesChannel={oppSalesChannel}
          />
        </section>
      )}

      <WrongPhoneNumberModal
        onUpdatePhoneNumber={onUpdatePhoneNumber}
        phoneNumber={caseInfo.phone}
        leadPhoneNumber={caseInfo.leadPhoneNumber}
        open={showWrongPhoneNumberModal}
        onOk={async (message: string) => {
          await onCaseClosed(
            buildBaseCaseComplete(caseInfo, {
              opportunityComments: message,
              caseStatus: CaseStatus.WrongNumber,
            }),
          );
          setShowWrongPhoneNumberModal(false);
        }}
        onCallButtonClick={handleCallButtonClick}
        disableCallButton={isAutodialerFlow}
        isAutodialerFlow={isAutodialerFlow}
        showModal={setShowWrongPhoneNumberModal}
      />

      <CustomerLostModal
        salesChannel={caseInfo.salesChannel!}
        inboundCenter={inboundCenter}
        isAutodialerFlow={isAutodialerFlow}
        open={showCustomerLost}
        useConfigurableLostReasons={useCloseCaseFlow}
        lostReasonsReady={lostReasonsReady}
        lostReasonOptions={lostReasonOptions}
        onOk={async (customerLostInfo: CustomerLostInfo) => {
          try {
            await onCaseClosed(
              buildBaseCaseComplete(caseInfo, {
                customerLostReasons: customerLostInfo.reasons,
                customerLostDescription: customerLostInfo.description,
                caseStatus: CaseStatus.CustomerLost,
                shareContactData: customerLostInfo.shareContactData,
                isDataDeletionRequested: customerLostInfo.isDataDeletionRequested,
              }),
            );
            setShowCustomerLost(false);
            if (inboundCenter && !isAutodialerFlow) {
              onInboundCallback?.(undefined);
            }
          } catch (error) {
            console.error('Failed to close case as customer lost:', error);
            throw error;
          }
        }}
        onCancel={() => setShowCustomerLost(false)}
        customerDeleteData={{
          bookingAgentFullName: accounts[0]?.name ?? '',
          customerFullName: `${caseInfo.firstName} ${caseInfo.lastName}`,
          contactUrl: `${salesForceRootUrl}/lightning/r/Opportunity/${caseInfo.opportunityId}/view`,
        }}
      />

      <NotReachedModal
        open={showNotReachedModal}
        onCancel={() => setShowNotReachedModal(false)}
        onOk={async (mailbox: boolean) => {
          await onCaseClosed(buildBaseCaseComplete(caseInfo, { caseStatus: CaseStatus.NotReached, mailbox }));
          setShowNotReachedModal(false);
        }}
      />

      <ReachedButNotBookedBody
        open={showReachedButNotBookedBody}
        onCancel={() => setShowReachedButNotBookedBody(false)}
        inboundCenter={inboundCenter}
        isAutodialerFlow={isAutodialerFlow}
        onOk={async (reachedButNotBookedInfo: ReachedButNotBookedInfo) => {
          await onCaseClosed(
            buildBaseCaseComplete(caseInfo, {
              doNotCallBefore: reachedButNotBookedInfo.dateToCall.toDate(),
              caseStatus: CaseStatus.ReachedButNotBooked,
              notBookedReason: reachedButNotBookedInfo.notBookedReason,
            }),
          );
          setShowReachedButNotBookedBody(false);
          if (inboundCenter && !isAutodialerFlow) {
            onInboundCallback?.(undefined);
          }
        }}
      />
    </div>
  );
};
